<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="rounded-lg ledger-dense-form">
      <h4 class="mb-50">
        {{ isEdit ? $t('ledger_inv_edit') : $t('ledger_inv_new') }}
      </h4>
      <b-row>
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group :label="$t('ledger_payroll_project')">
            <v-select
              :value="projectOption"
              :dir="isRtl ? 'rtl' : 'ltr'"
              :options="projectOptions"
              label="label"
              :filterable="false"
              :placeholder="$t('ledger_payroll_project')"
              class="ledger-account-select"
              @search="searchProjects"
              @input="onProject"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="!form.project_id"
          cols="12"
          md="3"
        >
          <b-form-group :label="$t('ledger_inv_customer')">
            <v-select
              :value="customerOption"
              :dir="isRtl ? 'rtl' : 'ltr'"
              :options="customerOptions"
              label="label"
              :filterable="false"
              :placeholder="$t('ledger_inv_customer')"
              class="ledger-account-select"
              @search="searchCustomers"
              @input="onCustomer"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="!form.project_id"
          cols="12"
          md="3"
        >
          <b-form-group :label="$t('ledger_payroll_activity')">
            <b-form-select
              v-model="form.gl_activity"
              :options="activityOptions"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-group :label="$t('ledger_inv_date')">
            <b-form-input
              v-model="form.invoice_date"
              type="date"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-group :label="$t('ledger_inv_due')">
            <b-form-input
              v-model="form.due_date"
              type="date"
              @input="dueTouched = true"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group :label="$t('ledger_assets_notes')">
            <b-form-input v-model="form.notes" />
          </b-form-group>
        </b-col>
      </b-row>

      <div
        v-for="(line, index) in form.lines"
        :key="index"
        class="ledger-inv-line"
      >
        <b-row>
          <b-col
            cols="12"
            lg="3"
          >
            <b-form-group :label="index === 0 ? $t('ledger_inv_item') : null">
              <v-select
                :value="line.itemOption"
                :dir="isRtl ? 'rtl' : 'ltr'"
                :options="itemOptionsFor(line)"
                label="label"
                :filterable="false"
                :placeholder="$t('ledger_inv_item_ph')"
                class="ledger-account-select"
                @open="searchItems('', null)"
                @search="searchItems"
                @input="onItem(index, $event)"
              />
            </b-form-group>
          </b-col>
          <b-col
            v-if="line.item_id"
            cols="6"
            lg="2"
          >
            <b-form-group :label="index === 0 ? $t('ledger_inv_warehouse') : null">
              <v-select
                v-model="line.warehouse_id"
                :dir="isRtl ? 'rtl' : 'ltr'"
                :options="warehouseChoices(line)"
                :reduce="option => option.id"
                label="label"
                :placeholder="$t('ledger_inv_warehouse')"
                class="ledger-account-select"
              />
              <small
                v-if="!line.warehouse_id"
                class="text-danger"
              >
                {{ $t('ledger_inv_warehouse_required') }}
              </small>
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            lg="2"
          >
            <b-form-group :label="index === 0 ? $t('ledger_type') : null">
              <b-form-select
                v-model="line.type"
                :options="lineTypeOptions"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            lg="2"
          >
            <b-form-group :label="index === 0 ? $t('ledger_description') : null">
              <b-form-input
                v-model="line.description"
                :title="line.item_id ? $t('ledger_inv_desc_from_item') : null"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="4"
            lg="1"
          >
            <b-form-group :label="index === 0 ? $t('ledger_maps_qty') : null">
              <b-form-input
                v-model.number="line.quantity"
                type="number"
                min="0"
                step="0.001"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="4"
            lg="1"
          >
            <b-form-group :label="index === 0 ? $t('ledger_maps_unit_price') : null">
              <b-form-input
                v-model.number="line.unit_price"
                type="number"
                min="0"
                step="0.01"
                :title="line.item_id ? $t('ledger_inv_price_from_item') : null"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="2"
            lg="1"
            class="d-flex align-items-end"
          >
            <b-button
              v-if="form.lines.length > 1"
              variant="outline-danger"
              size="sm"
              @click="form.lines.splice(index, 1)"
            >
              ×
            </b-button>
          </b-col>
        </b-row>
      </div>
      <b-button
        variant="outline-primary"
        size="sm"
        class="mb-1"
        @click="addLine"
      >
        {{ $t('ledger_inv_add_line') }}
      </b-button>

      <div class="ledger-inv-totals ledger-num">
        <div>{{ $t('ledger_inv_services') }} {{ money(totals.service) }}</div>
        <div v-if="allowFees">
          {{ $t('ledger_inv_fees') }} {{ money(totals.fees) }}
        </div>
        <div>{{ $t('ledger_inv_discount') }} {{ money(totals.discount) }}</div>
        <strong>{{ $t('ledger_inv_total') }} {{ money(totals.total) }}</strong>
      </div>
      <p
        v-if="projectContract"
        class="text-danger"
      >
        {{ $t('ledger_inv_via_contract') }}
        <b-link
          v-if="projectContract.id && hasPermission('view contracts')"
          :to="{ name: 'ledger-contracts-view', params: { id: projectContract.id } }"
        >
          {{ $t('ledger_ct_title') }}
        </b-link>
      </p>
      <p
        v-else-if="activity === 'contracting'"
        class="text-danger"
      >
        {{ $t('ledger_ct_contracting_only') }}
        <b-link
          v-if="hasPermission('view contracts')"
          :to="{ name: 'ledger-contracts' }"
        >
          {{ $t('ledger_ct_title') }}
        </b-link>
      </p>
      <p
        v-else-if="activity === 'maps'"
        class="text-danger"
      >
        {{ $t('ledger_inv_maps_sales') }}
      </p>
      <p
        v-if="discountTooHigh"
        class="text-danger"
      >
        {{ $t('ledger_inv_discount_limit') }}
      </p>
      <p
        v-if="hasItemLine"
        class="text-muted small"
      >
        {{ $t('ledger_inv_stock_note') }}
      </p>

      <b-button
        variant="primary"
        class="rounded-pill"
        :disabled="saving || !canSave"
        @click="save"
      >
        {{ $t('ledger_inv_save_draft') }}
      </b-button>
    </b-card>
    <div class="ledger-inv-sticky d-md-none">
      <span>{{ $t('ledger_inv_total') }}</span>
      <strong class="ledger-num">{{ money(totals.total) }}</strong>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BFormSelect, BButton, BLink,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Projects from '@/api/projects/projects'
import Customers from '@/api/customers/customers'
import { createInvoice, getInvoice, updateInvoice } from '@/api/accounting/invoices'
import { getItems } from '@/api/accounting/items'
import { getWarehouses } from '@/api/accounting/warehouses'
import { getProjectCost } from '@/api/accounting/projectCost'
import {
  listFrom, oneFrom, collectPages, enumValue, formatLedgerMoney, roundMoney, todayIso, apiErrorMessage, ledgerMessageKey,
} from '@/utils/ledger'

const projectsApi = new Projects()
const customersApi = new Customers()

function addDays(iso, days) {
  const date = new Date(`${iso}T00:00:00`)
  date.setDate(date.getDate() + days)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function itemToOption(item) {
  if (!item) return null
  const kind = item.kind && typeof item.kind === 'object' ? (item.kind.label_ar || '') : ''
  const base = [item.code, item.name].filter(Boolean).join(' — ')
  return { id: item.id, label: kind ? `${base} · ${kind}` : base, raw: item }
}

function blankLine() {
  return {
    type: 'service',
    description: '',
    quantity: 1,
    unit_price: null,
    item_id: null,
    warehouse_id: null,
    itemOption: null,
    warehouse: null,
  }
}

export default {
  name: 'InvoiceForm',
  components: {
    BCard, BRow, BCol, BFormGroup, BFormInput, BFormSelect, BButton, BLink, vSelect,
  },
  data() {
    const today = todayIso()
    return {
      saving: false,
      dueTouched: false,
      projectOptions: [],
      customerOptions: [],
      itemSearchOptions: [],
      warehouses: [],
      projectOption: null,
      customerOption: null,
      projectActivity: null,
      projectContract: null,
      checkingProject: false,
      form: {
        project_id: null,
        customer_id: null,
        gl_activity: 'survey',
        invoice_date: today,
        due_date: addDays(today, 30),
        notes: '',
        lines: [blankLine()],
      },
    }
  },
  computed: {
    isRtl() {
      return this.$store.state.appConfig.layout.isRTL
    },
    isEdit() {
      return Boolean(this.$route.params.id)
    },
    activity() {
      return this.form.project_id ? (this.projectActivity || 'survey') : this.form.gl_activity
    },
    allowFees() {
      return this.activity === 'licensing'
    },
    activityOptions() {
      return [
        { value: 'survey', text: this.$t('ledger_inv_activity_survey') },
        { value: 'licensing', text: this.$t('ledger_inv_activity_licensing') },
      ]
    },
    lineTypeOptions() {
      const options = [
        { value: 'service', text: this.$t('ledger_inv_line_service') },
        { value: 'discount', text: this.$t('ledger_inv_line_discount') },
      ]
      if (this.allowFees) {
        options.splice(1, 0, { value: 'government_fee', text: this.$t('ledger_inv_line_fee') })
      }
      return options
    },
    totals() {
      const sum = type => this.form.lines
        .filter(line => line.type === type)
        .reduce((acc, line) => roundMoney(acc + ((Number(line.quantity) || 0) * (Number(line.unit_price) || 0))), 0)
      const service = sum('service')
      const fees = this.allowFees ? sum('government_fee') : 0
      const discount = sum('discount')
      return { service, fees, discount, total: roundMoney(service + fees - discount) }
    },
    discountTooHigh() {
      return this.totals.discount > this.totals.service
    },
    hasItemLine() {
      return this.form.lines.some(line => line.item_id)
    },
    canSave() {
      if (this.checkingProject || this.projectContract) return false
      if (this.activity === 'contracting' || this.activity === 'maps') return false
      if (this.discountTooHigh) return false
      if (!this.form.lines.length) return false
      if (!this.form.project_id && !this.form.customer_id) return false
      return this.form.lines.every(line => this.lineReady(line))
    },
  },
  watch: {
    'form.invoice_date': function onDate(value) {
      if (!this.dueTouched && value) this.form.due_date = addDays(value, 30)
    },
    allowFees(value) {
      if (!value) {
        this.form.lines.forEach(line => {
          if (line.type === 'government_fee') line.type = 'service'
        })
      }
    },
  },
  mounted() {
    this.loadWarehouses()
    if (this.isEdit) this.loadInvoice()
  },
  beforeDestroy() {
    clearTimeout(this.itemTimer)
  },
  methods: {
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    addLine() {
      this.form.lines.push(blankLine())
    },
    lineReady(line) {
      if (line.item_id) {
        if (!line.warehouse_id) return false
        if (line.unit_price === null || line.unit_price === '') return true
        return Number(line.unit_price) >= 0
      }
      return Number(line.unit_price) >= 0 && line.unit_price !== null && line.unit_price !== ''
    },
    itemOptionsFor(line) {
      const options = [...this.itemSearchOptions]
      if (line.itemOption && !options.some(option => option.id === line.itemOption.id)) {
        options.unshift(line.itemOption)
      }
      return options
    },
    warehouseChoices(line) {
      const rows = [...this.warehouses]
      if (line.warehouse && line.warehouse.id && !rows.some(row => row.id === line.warehouse.id)) {
        rows.unshift(line.warehouse)
      }
      return rows.map(warehouse => ({
        id: warehouse.id,
        label: [warehouse.code, warehouse.name].filter(Boolean).join(' — '),
      }))
    },
    onItem(index, option) {
      const line = this.form.lines[index]
      line.itemOption = option
      line.item_id = option ? option.id : null
      if (!option) {
        line.warehouse_id = null
        line.warehouse = null
        return
      }
      const raw = option.raw || {}
      line.description = raw.name || ''
      const price = raw.sale_price
      line.unit_price = price === null || price === undefined || price === '' ? null : Number(price)
      this.loadWarehouses()
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
    searchProjects(search, loading) {
      const q = (search || '').trim()
      if (q.length < 1) return
      if (loading) loading(true)
      clearTimeout(this.projectTimer)
      this.projectTimer = setTimeout(async () => {
        try {
          const res = await projectsApi.listProjects({ search: q, per_page: 20, page: 1 })
          this.projectOptions = listFrom(res).map(project => ({
            id: project.id,
            label: `${project.project_number || project.id} — ${project.name || ''}`,
            raw: project,
          }))
        } catch (error) {
          this.projectOptions = []
        } finally {
          if (loading) loading(false)
        }
      }, 300)
    },
    searchCustomers(search, loading) {
      const q = (search || '').trim()
      if (q.length < 1) return
      if (loading) loading(true)
      clearTimeout(this.customerTimer)
      this.customerTimer = setTimeout(async () => {
        try {
          const res = await customersApi.listCustomers({ search: q, per_page: 20, page: 1 })
          this.customerOptions = listFrom(res).map(customer => ({
            id: customer.id,
            label: customer.name || customer.customer_number || `#${customer.id}`,
            raw: customer,
          }))
        } catch (error) {
          this.customerOptions = []
        } finally {
          if (loading) loading(false)
        }
      }, 300)
    },
    inferActivity(project) {
      if (!project) return null
      const direct = enumValue(project.gl_activity)
      if (direct === 'survey' || direct === 'licensing' || direct === 'contracting' || direct === 'maps') return direct
      const service = project.service_type || project.serviceType || {}
      const activity = enumValue(service.activity) || enumValue(service.gl_activity)
      if (activity === 'survey' || activity === 'licensing' || activity === 'contracting' || activity === 'maps') return activity
      const text = `${service.name || ''} ${service.name_ar || ''} ${project.service_type_name || ''}`.toLowerCase()
      if (text.includes('contract') || text.includes('مقاول')) return 'contracting'
      if (text.includes('licen') || text.includes('ترخيص')) return 'licensing'
      if (text.includes('map') || text.includes('خريط')) return 'maps'
      return 'survey'
    },
    onProject(option) {
      this.projectOption = option
      this.form.project_id = option ? option.id : null
      this.projectActivity = option ? this.inferActivity(option.raw) : null
      this.projectContract = null
      if (option && option.raw) {
        const customer = option.raw.customer
        this.form.customer_id = option.raw.customer_id || (customer && customer.id) || null
      }
      if (option) this.loadProjectContract(option.id)
    },
    async loadProjectContract(projectId) {
      this.checkingProject = true
      try {
        const cost = oneFrom(await getProjectCost(projectId))
        this.projectContract = cost && cost.contract ? cost.contract : null
      } catch (error) {
        this.projectContract = null
      } finally {
        this.checkingProject = false
      }
    },
    onCustomer(option) {
      this.customerOption = option
      this.form.customer_id = option ? option.id : null
    },
    searchItems(search, loading) {
      if (!this.hasPermission('view items')) return
      if (loading) loading(true)
      clearTimeout(this.itemTimer)
      this.itemTimer = setTimeout(async () => {
        try {
          const params = { per_page: 25 }
          const keyword = String(search || '').trim()
          if (keyword) params.keyword = keyword
          const res = await getItems(params)
          this.itemSearchOptions = listFrom(res).map(itemToOption)
        } catch (error) {
          this.itemSearchOptions = []
          if (error?.response?.status === 422) {
            this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'warning')
          }
        } finally {
          if (loading) loading(false)
        }
      }, 300)
    },
    async loadWarehouses() {
      if (!this.hasPermission('view warehouses') || this.warehouses.length) return
      try {
        const first = await getWarehouses({ per_page: 200 })
        this.warehouses = await collectPages(page => getWarehouses({ per_page: 200, page }), first)
      } catch (error) {
        this.warehouses = []
        if (error?.response?.status === 422) {
          this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'warning')
        }
      }
    },
    payload() {
      const body = {
        invoice_date: this.form.invoice_date,
        due_date: this.form.due_date,
        lines: this.form.lines.map(line => {
          const row = {
            type: line.type,
            quantity: Number(line.quantity) || 1,
          }
          const description = String(line.description || '').trim()
          if (description) row.description = description
          if (line.item_id) {
            row.item_id = line.item_id
            row.warehouse_id = line.warehouse_id
            if (line.unit_price !== null && line.unit_price !== '') row.unit_price = roundMoney(line.unit_price)
          } else {
            row.unit_price = roundMoney(line.unit_price)
          }
          return row
        }),
      }
      const notes = String(this.form.notes || '').trim()
      if (notes) body.notes = notes
      if (this.form.project_id) body.project_id = this.form.project_id
      else {
        body.customer_id = this.form.customer_id
        body.gl_activity = this.form.gl_activity
      }
      return body
    },
    async loadInvoice() {
      try {
        const res = await getInvoice(this.$route.params.id)
        const invoice = oneFrom(res)
        if (enumValue(invoice.status) !== 'draft') {
          this.$router.replace({ name: 'ledger-invoices-view', params: { id: invoice.id } })
          return
        }
        this.form.project_id = invoice.project_id
        this.form.customer_id = invoice.customer_id
        this.form.gl_activity = invoice.gl_activity || 'survey'
        this.form.invoice_date = invoice.invoice_date
        this.form.due_date = invoice.due_date
        this.form.notes = invoice.notes || ''
        this.projectActivity = invoice.gl_activity || 'survey'
        this.dueTouched = true
        if (invoice.project) {
          this.projectOption = {
            id: invoice.project.id,
            label: `${invoice.project.project_number || invoice.project.id} — ${invoice.project.name || ''}`,
            raw: invoice.project,
          }
          this.projectActivity = this.inferActivity(invoice.project)
        }
        if (invoice.project_id) this.loadProjectContract(invoice.project_id)
        if (invoice.customer) {
          this.customerOption = { id: invoice.customer.id, label: invoice.customer.name, raw: invoice.customer }
        }
        this.form.lines = (invoice.lines || []).map(line => ({
          type: enumValue(line.type) || 'service',
          description: line.description || '',
          quantity: Number(line.quantity) || 1,
          unit_price: line.unit_price === null || line.unit_price === undefined || line.unit_price === '' ? null : Number(line.unit_price),
          item_id: line.item_id || (line.item && line.item.id) || null,
          warehouse_id: line.warehouse_id || (line.warehouse && line.warehouse.id) || null,
          itemOption: line.item
            ? itemToOption(line.item)
            : (line.item_id ? { id: line.item_id, label: `#${line.item_id}`, raw: {} } : null),
          warehouse: line.warehouse || null,
        }))
        if (!this.form.lines.length) this.form.lines = [blankLine()]
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      }
    },
    async save() {
      if (!this.canSave || this.saving) return
      this.saving = true
      try {
        const res = this.isEdit
          ? await updateInvoice(this.$route.params.id, this.payload())
          : await createInvoice(this.payload())
        const invoice = oneFrom(res)
        this.toast(this.$t('ledger_inv_saved'), 'success')
        if (invoice && invoice.id) {
          this.$router.push({ name: 'ledger-invoices-view', params: { id: invoice.id } })
        }
      } catch (error) {
        const message = apiErrorMessage(error, this.$t('ledger_error'))
        const key = ledgerMessageKey(message)
        this.toast(key ? this.$t(key) : message, error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
.ledger-num { font-variant-numeric: tabular-nums; }
.ledger-inv-line { border-bottom: 1px solid #f0f0f0; margin-bottom: 0.25rem; }
.ledger-inv-totals { display: grid; gap: 0.25rem; margin: 0.5rem 0; }
.ledger-dense-form .card-body { padding: 0.75rem 1rem; }
.ledger-dense-form .form-group { margin-bottom: 0.35rem; }
.ledger-dense-form .form-group > label {
  font-size: 0.75rem;
  margin-bottom: 0.1rem;
}
.ledger-dense-form .form-control,
.ledger-dense-form .custom-select {
  height: 1.85rem;
  min-height: 1.85rem;
  padding: 0.15rem 0.45rem;
  font-size: 0.8125rem;
}
.ledger-dense-form .vs__dropdown-toggle {
  min-height: 1.85rem;
  padding-top: 0;
  padding-bottom: 0;
}
.ledger-dense-form .vs__selected,
.ledger-dense-form .vs__search {
  margin-top: 1px;
  margin-bottom: 1px;
  font-size: 0.8125rem;
}
.ledger-inv-sticky {
  position: fixed; bottom: 0; inset-inline: 0; z-index: 20; display: flex;
  justify-content: space-between; background: #fff; border-top: 1px solid #e6e6e6;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
</style>
